import {
  splitMetricsByLegalEntity,
  type ICalculateMetricsInputModel,
  type ILegalEntitySliceMeta,
} from '@foresightdata/metrics-calculator';
import { ILegalStructureGraphNode } from '../../../services/queries/MaggieLegalStructureQueries';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import {
  IPortfolioTreeBuild,
  IPortfolioTreeInput,
  IPortfolioTreeLeaf,
  IPortfolioTreeNode,
  PortfolioTreeMode,
} from './portfolioTree.types';

/** Names an entity in the graph: one entry however many places it is held. */
const graphKey = (nodeType: string, nodeId: number) => `${nodeType}-${nodeId}`;

/** Names a node in the tree: path-unique, so a vehicle held by two funds is two nodes. */
const treeKeyFor = (path: string[]) => path.join('/');

/** Real structures are a handful of hops deep; the cap keeps a cyclic graph from fanning out. */
const MAX_VEHICLE_HOPS = 8;

interface IGraphIndex {
  nodes: Map<string, ILegalStructureGraphNode>;
  equityChildren: Map<string, string[]>;
  equityParents: Map<string, string[]>;
  familyOf: Map<string, string>; // fund key → family key (MEMBERSHIP)
  fundKeyByFundId: Map<number, string>;
  vehicleKeyByFundId: Map<number, string>; // paired intermediary fund → company-<id>
}

function indexGraphs(graphs: IPortfolioTreeInput['graphs']): IGraphIndex {
  const index: IGraphIndex = {
    nodes: new Map(),
    equityChildren: new Map(),
    equityParents: new Map(),
    familyOf: new Map(),
    fundKeyByFundId: new Map(),
    vehicleKeyByFundId: new Map(),
  };
  for (const graph of graphs) {
    for (const node of graph.nodes) {
      const key = graphKey(node.ref.nodeType, node.ref.nodeId);
      index.nodes.set(key, node);
      if (node.ref.nodeType === 'fund') {
        index.fundKeyByFundId.set(node.ref.nodeId, key);
      }
      if (node.ref.nodeType === 'company' && node.fundId != null && node.kind !== 'PORTFOLIO_COMPANY') {
        index.vehicleKeyByFundId.set(node.fundId, key);
      }
    }
    for (const edge of graph.edges) {
      const parent = graphKey(edge.parentType, edge.parentId);
      const child = graphKey(edge.childType, edge.childId);
      if (edge.relationshipType === 'MEMBERSHIP') {
        index.familyOf.set(child, parent);
      } else if (edge.relationshipType === 'EQUITY') {
        if (!index.equityChildren.has(parent)) {
          index.equityChildren.set(parent, []);
        }
        index.equityChildren.get(parent)!.push(child);
        if (!index.equityParents.has(child)) {
          index.equityParents.set(child, []);
        }
        index.equityParents.get(child)!.push(parent);
      }
    }
  }
  return index;
}

// Shortest EQUITY path from a fund to a vehicle (BFS over the active subgraph); [] when unreachable.
function vehiclePath(index: IGraphIndex, fromKey: string, toKey: string): string[] {
  const queue: string[][] = [[fromKey]];
  const seen = new Set([fromKey]);
  while (queue.length > 0) {
    const path = queue.shift()!;
    const last = path[path.length - 1]!;
    if (last === toKey) {
      return path.slice(1);
    }
    for (const child of index.equityChildren.get(last) ?? []) {
      if (!seen.has(child)) {
        seen.add(child);
        queue.push([...path, child]);
      }
    }
  }
  return [];
}

/** Every EQUITY chain `[fundKey, ...entity hops]` the graph gives from a fund down to `toKey`. */
function graphChainsToFund(index: IGraphIndex, toKey: string): string[][] {
  const chains: string[][] = [];
  const walk = (key: string, below: string[], seen: Set<string>) => {
    if (below.length >= MAX_VEHICLE_HOPS) {
      return;
    }
    for (const parent of index.equityParents.get(key) ?? []) {
      if (seen.has(parent)) {
        continue;
      }
      if (parent.startsWith('fund-')) {
        chains.push([parent, key, ...below]);
      } else {
        walk(parent, [key, ...below], new Set([...seen, parent]));
      }
    }
  };
  walk(toKey, [], new Set([toKey]));
  return chains;
}

function ancestry(index: IGraphIndex, fundKey: string): string[] {
  const family = index.familyOf.get(fundKey);
  return family ? [family, fundKey] : [fundKey];
}

export function buildPortfolioTree(input: IPortfolioTreeInput): IPortfolioTreeBuild {
  const index = indexGraphs(input.graphs);
  const nodes = new Map<string, IPortfolioTreeNode>();
  const leaves: IPortfolioTreeLeaf[] = [];
  const leafKeysByNode = new Map<string, string[]>();
  const incompleteNodes = new Set<string>();

  // `key` is the path-unique tree key; `entityKey` is the graph key label, kind and `nodeRef`
  // are read from. They coincide for families and funds and diverge for vehicles.
  const ensureNode = (key: string, path: string[], entityKey: string): IPortfolioTreeNode => {
    const existing = nodes.get(key);
    if (existing) {
      return existing;
    }
    const graphNode = index.nodes.get(entityKey);
    const [nodeType, id] = entityKey.split('-') as [IPortfolioTreeNode['nodeRef']['nodeType'], string];
    const node: IPortfolioTreeNode = {
      key,
      kind: nodeType === 'family' ? 'family' : nodeType === 'fund' ? 'fund' : 'vehicle',
      label:
        graphNode?.name ??
        (nodeType === 'fund' ? (input.fundsById.get(Number(id))?.name ?? entityKey) : entityKey),
      path,
      nodeRef: { nodeType, nodeId: Number(id) },
      incomplete: false,
      source: 'estimated',
    };
    nodes.set(key, node);
    return node;
  };

  const companyNameById = new Map(input.metrics.map((m) => [m.companyId, m.company?.name]));

  const addLeaf = (
    slice: ICalculateMetricsInputModel & ILegalEntitySliceMeta,
    parentPath: string[],
    leafKey: string,
    listUnder: string[]
  ) => {
    const companyName = companyNameById.get(slice.companyId) ?? `Company ${slice.companyId}`;
    const node: IPortfolioTreeNode = {
      key: leafKey,
      kind: 'company',
      label: companyName,
      path: [...parentPath, leafKey],
      nodeRef: { nodeType: 'company', nodeId: slice.companyId },
      incomplete: slice.sliceIncomplete,
      source: 'ledger',
    };
    nodes.set(leafKey, node);
    leaves.push({ node, slice });
    for (const ancestorKey of listUnder) {
      if (!leafKeysByNode.has(ancestorKey)) {
        leafKeysByNode.set(ancestorKey, []);
      }
      leafKeysByNode.get(ancestorKey)!.push(leafKey);
      if (slice.sliceIncomplete) {
        incompleteNodes.add(ancestorKey);
      }
    }
  };

  const mode: PortfolioTreeMode = input.mode;

  // A vehicle has one node per parent path, so every question about it is per (path, company).
  const vehiclePathsByEntity = new Map<string, { key: string; fundId: number }[]>();
  const lookThroughPaths = new Set<string>();
  const pathCompany = (pathKey: string, companyId: number) => `${pathKey}|${companyId}`;
  const registerVehiclePath = (entityKey: string, key: string, fundId: number) => {
    const known = vehiclePathsByEntity.get(entityKey);
    if (!known) {
      vehiclePathsByEntity.set(entityKey, [{ key, fundId }]);
    } else if (!known.some((path) => path.key === key)) {
      known.push({ key, fundId });
    }
  };

  interface IPendingLeaf {
    slice: (typeof slices)[number];
    parentPath: string[];
    leafKey: string;
    base: string[];
    lastVehicle: string | undefined;
    hasVehicle: boolean;
  }
  const pending: IPendingLeaf[] = [];
  const ownBooksPending: {
    slice: (typeof slices)[number];
    entityKey: string;
    fundId: number;
    /** Entity keys of the vehicle hops below the intermediary's own vehicle, if any. */
    subHops: string[];
    legalEntityId: number | null;
  }[] = [];

  // The leaves wait: where a vehicle's own books land depends on every path existing first.
  const slices = splitMetricsByLegalEntity(
    input.metrics as unknown as (IMetricsDataModel & ICalculateMetricsInputModel)[]
  );
  for (const slice of slices) {
    if (slice.familyId != null) {
      const familyKey = graphKey('family', slice.familyId);
      ensureNode(familyKey, [familyKey], familyKey);
      pending.push({
        slice,
        parentPath: [familyKey],
        leafKey: `leaf-${slice.companyId}-family${slice.familyId}-direct`,
        base: [familyKey],
        lastVehicle: undefined,
        hasVehicle: false,
      });
      continue;
    }
    const fundId = slice.transactions[0]!.fundId;
    // A paired intermediary fund is the vehicle's own books, never a top-level fund row; a fund the
    // config knows to be real vetoes the pairing, which comes off a possibly stale legal-entity id.
    const pairedVehicle = index.vehicleKeyByFundId.get(fundId);
    if (pairedVehicle && input.fundsById.get(fundId)?.fundKind !== 'fund') {
      const bookedTo = slice.legalEntityId == null ? null : graphKey('company', slice.legalEntityId);
      const subHops =
        bookedTo != null && bookedTo !== pairedVehicle ? vehiclePath(index, pairedVehicle, bookedTo) : [];
      ownBooksPending.push({
        slice: {
          ...slice,
          sliceIncomplete:
            slice.sliceIncomplete || (bookedTo != null && bookedTo !== pairedVehicle && subHops.length === 0),
        },
        entityKey: pairedVehicle,
        fundId,
        subHops,
        legalEntityId: slice.legalEntityId,
      });
      continue;
    }
    const fundKey = index.fundKeyByFundId.get(fundId) ?? graphKey('fund', fundId);
    const base = ancestry(index, fundKey);
    base.forEach((key, i) => ensureNode(key, base.slice(0, i + 1), key));
    // Graph keys of the equity hops, and the path-unique tree key each one gets under *this* fund.
    let vehicleEntityKeys: string[] = [];
    let vehicleKeys: string[] = [];
    let incomplete = slice.sliceIncomplete;
    if (slice.legalEntityId != null) {
      vehicleEntityKeys = vehiclePath(index, fundKey, graphKey('company', slice.legalEntityId));
      if (vehicleEntityKeys.length === 0) {
        incomplete = true;
      }
      vehicleKeys = vehicleEntityKeys.map((_, i) =>
        treeKeyFor([...base, ...vehicleEntityKeys.slice(0, i + 1)])
      );
      vehicleKeys.forEach((key, i) => {
        ensureNode(key, [...base, ...vehicleKeys.slice(0, i + 1)], vehicleEntityKeys[i]!);
        registerVehiclePath(vehicleEntityKeys[i]!, key, fundId);
        // Every hop on the chain looks through to this company, not just the booking one.
        lookThroughPaths.add(pathCompany(key, slice.companyId));
      });
    }
    pending.push({
      slice: { ...slice, sliceIncomplete: incomplete },
      parentPath: [...base, ...vehicleKeys],
      leafKey: `leaf-${slice.companyId}-${fundId}-${slice.legalEntityId ?? 'direct'}`,
      base,
      lastVehicle: vehicleKeys[vehicleKeys.length - 1],
      hasVehicle: vehicleKeys.length > 0,
    });
  }

  // A vehicle with own books needs every place the graph puts it, not only those a look-through reached.
  for (const { entityKey } of ownBooksPending) {
    for (const chain of graphChainsToFund(index, entityKey)) {
      const fundKey = chain[0]!;
      const fundId = Number(fundKey.slice('fund-'.length));
      const base = ancestry(index, fundKey);
      base.forEach((key, i) => ensureNode(key, base.slice(0, i + 1), key));
      const hops = chain.slice(1);
      const hopKeys = hops.map((_, i) => treeKeyFor([...base, ...hops.slice(0, i + 1)]));
      hopKeys.forEach((key, i) => {
        ensureNode(key, [...base, ...hopKeys.slice(0, i + 1)], hops[i]!);
        registerVehiclePath(hops[i]!, key, fundId);
      });
    }
  }

  for (const leaf of pending) {
    // Never empty in either mode: the pooling pass and `computeTreeRows` rely on every leaf being listed.
    const listUnder =
      mode === 'rollup' ? leaf.parentPath : !leaf.hasVehicle ? leaf.base : [leaf.lastVehicle!];
    addLeaf(leaf.slice, leaf.parentPath, leaf.leafKey, listUnder);
  }

  // Own books hang under one parent path that does not already look through to that company; lowest
  // fund id then path, so the receiving parent does not depend on transaction order.
  for (const { slice, entityKey, fundId, subHops, legalEntityId } of ownBooksPending) {
    const target = (vehiclePathsByEntity.get(entityKey) ?? [])
      .filter((path) => !lookThroughPaths.has(pathCompany(path.key, slice.companyId)))
      .sort((a, b) => a.fundId - b.fundId || a.key.localeCompare(b.key))[0];
    // Root-level only when the graph gives the entity no parent at all.
    const vehicle = target
      ? nodes.get(target.key)!
      : vehiclePathsByEntity.has(entityKey)
        ? undefined
        : ensureNode(entityKey, [entityKey], entityKey);
    if (!vehicle) {
      // Nowhere left to hang: mark the vehicle's rows rather than let the money leave silently.
      for (const path of vehiclePathsByEntity.get(entityKey) ?? []) {
        incompleteNodes.add(path.key);
      }
      continue;
    }
    const hopKeys = subHops.map((_, i) => treeKeyFor([vehicle.key, ...subHops.slice(0, i + 1)]));
    hopKeys.forEach((key, i) => ensureNode(key, [...vehicle.path, ...hopKeys.slice(0, i + 1)], subHops[i]!));
    const parentPath = [...vehicle.path, ...hopKeys];
    // Keyed by where it terminates: direct and via-sub-vehicle are two positions in one company.
    addLeaf(
      slice,
      parentPath,
      `leaf-${slice.companyId}-${fundId}-own-${legalEntityId ?? 'direct'}`,
      mode === 'rollup' ? parentPath : [hopKeys[hopKeys.length - 1] ?? vehicle.key]
    );
  }

  // Vehicles under a fund that no slice reached: the fund books its stake at entity level. Must run
  // after placement, so a node created here cannot divert an own-books slice.
  const isVehicle = (entityKey: string) => {
    const graphNode = index.nodes.get(entityKey);
    return graphNode?.ref.nodeType === 'company' && graphNode.kind !== 'PORTFOLIO_COMPANY';
  };
  // Memo by tree key, not entity: a vehicle two routes reach is two path-unique nodes.
  const walked = new Set<string>();
  const walkVehicles = (entityKey: string, base: string[], chain: string[], hopKeys: string[]) => {
    if (chain.length >= MAX_VEHICLE_HOPS) {
      return;
    }
    for (const child of index.equityChildren.get(entityKey) ?? []) {
      if (chain.includes(child) || !isVehicle(child)) {
        continue;
      }
      const key = treeKeyFor([...base, ...chain, child]);
      ensureNode(key, [...base, ...hopKeys, key], child);
      if (walked.has(key)) {
        continue;
      }
      walked.add(key);
      walkVehicles(child, base, [...chain, child], [...hopKeys, key]);
    }
  };
  for (const node of [...nodes.values()]) {
    if (node.kind === 'fund') {
      walkVehicles(node.key, node.path, [], []);
    }
  }

  for (const node of nodes.values()) {
    if (node.kind !== 'company') {
      node.source = (leafKeysByNode.get(node.key)?.length ?? 0) > 0 ? 'ledger' : 'estimated';
      node.incomplete = incompleteNodes.has(node.key);
    }
  }

  // `leafKeysByNode` cannot say whether a leaf sits anywhere below a node: in individual mode a
  // node lists only the leaves that terminate on it.
  const entityKeyOf = (node: IPortfolioTreeNode) => graphKey(node.nodeRef.nodeType, node.nodeRef.nodeId);
  const withLedgerBelow = new Set<string>();
  // The ledger beats the register, which carries one figure per entity however many parents hold it.
  const ledgerEntitiesBelow = new Map<string, Set<string>>();
  for (const leaf of leaves) {
    leaf.node.path.forEach((segment, i) => {
      withLedgerBelow.add(segment);
      let below = ledgerEntitiesBelow.get(segment);
      if (!below) {
        below = new Set();
        ledgerEntitiesBelow.set(segment, below);
      }
      for (const descendant of leaf.node.path.slice(i + 1)) {
        const held = nodes.get(descendant);
        if (held) {
          below.add(entityKeyOf(held));
        }
      }
    });
  }
  const leafless = [...nodes.values()].filter(
    (node) => node.kind === 'vehicle' && !withLedgerBelow.has(node.key)
  );
  const parentKeyOf = (node: IPortfolioTreeNode) => node.path[node.path.length - 2];
  const heldDirectlyByFund = (node: IPortfolioTreeNode) =>
    nodes.get(parentKeyOf(node) ?? '')?.kind === 'fund';
  // Everything below a leafless vehicle is leafless too; pooling anything but the topmost of a
  // chain would double it.
  const leaflessKeys = new Set(leafless.map((node) => node.key));
  const shallowest = new Set(
    leafless.filter((node) => !leaflessKeys.has(parentKeyOf(node) ?? '')).map((node) => node.key)
  );
  const leaflessVehiclesByNode = new Map<string, string[]>();
  for (const node of nodes.values()) {
    // A leafless vehicle renders from its own register row; pooling would show its children's value.
    if (node.kind === 'company' || leaflessKeys.has(node.key)) {
      continue;
    }
    const alreadyBooked = ledgerEntitiesBelow.get(node.key);
    const pooledEntities = new Set<string>();
    const pooled: string[] = [];
    for (const vehicle of leafless) {
      const isUnder =
        mode === 'rollup'
          ? shallowest.has(vehicle.key) &&
            vehicle.path.length > node.path.length &&
            vehicle.path[node.path.length - 1] === node.key
          : parentKeyOf(vehicle) === node.key ||
            // Individual mode lists what a fund books directly under its whole ancestry.
            (heldDirectlyByFund(vehicle) && vehicle.path.slice(0, -2).includes(node.key));
      const entity = entityKeyOf(vehicle);
      if (!isUnder) {
        continue;
      }
      if (alreadyBooked?.has(entity)) {
        // The ledger covers only the paths it came from, so this node's number is short.
        node.incomplete = true;
        continue;
      }
      if (pooledEntities.has(entity)) {
        continue;
      }
      pooledEntities.add(entity);
      pooled.push(vehicle.key);
    }
    if (pooled.length > 0) {
      leaflessVehiclesByNode.set(node.key, pooled);
    }
  }

  return { nodes: [...nodes.values()], leaves, leafKeysByNode, leaflessVehiclesByNode };
}
